// ─── Help Content for all pages ───────────────────────────────────────────────
// Each page has: functionality, userGuide, faq, and tourSteps
// tourSteps map to data-help-tour="<id>" attributes placed on DOM elements.

export interface HelpFeature {
  title: string;
  description: string;
}

export interface UserGuideStep {
  step: number;
  text: string;
}

export interface UserGuideSection {
  title: string;
  intro?: string;
  steps: UserGuideStep[];
  note?: string;
  noteType?: 'info' | 'warning';
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HelpTourStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  icon: string;
}

export interface RelatedPage {
  label: string;
  page: string;
}

export interface PageHelpContent {
  pageTitle: string;
  pageDescription: string;
  functionality: {
    overview: string;
    features: HelpFeature[];
    sectionsAndFields?: string;
  };
  userGuide: {
    intro?: string;
    sections: UserGuideSection[];
  };
  faq: FAQItem[];
  tourSteps: HelpTourStep[];
  relatedPages?: RelatedPage[];
}

export type HelpPageKey =
  | 'calendar'
  | 'heatmap'
  | 'resources'
  | 'projects'
  | 'settings'
  | 'unallocated'
  | 'overview'
  | 'report-utilisation'
  | 'report-utilisation-forecast'
  | 'report-available-capacity'
  | 'report-resource-allocation'
  | 'report-project-staffing'
  | 'report-bench'
  | 'roles-permissions'
  | 'user-setup';

export const HELP_CONTENT: Record<HelpPageKey, PageHelpContent> = {

  // ─── Calendar / Planner ─────────────────────────────────────────────────────
  calendar: {
    pageTitle: 'Planner',
    pageDescription: 'Create, edit, and manage resource bookings across your team on a visual timeline.',
    functionality: {
      overview:
        'The Planner is your central workspace for scheduling resources across projects and time. You can create bookings by clicking or dragging on the timeline, reassign work by dragging existing bookings, and adjust durations by resizing. The planner updates in real time and reflects current utilisation across your team.',
      features: [
        {
          title: 'Create Booking',
          description: 'Click any empty cell on the timeline to open the booking form and create a new booking for that resource and date range.',
        },
        {
          title: 'Drag & Drop',
          description: 'Drag an existing booking to a different row or date to reassign it. The scheduler will validate capacity and warn on conflicts.',
        },
        {
          title: 'Resize Bookings',
          description: 'Drag the left or right edge of a booking to extend or shorten its duration. Utilisation recalculates automatically.',
        },
        {
          title: 'Booking Form',
          description: 'A full-featured side drawer for entering project, booking type, dates, hours, notes, custom fields, and recurring patterns.',
        },
        {
          title: 'Filters & Search',
          description: 'Filter the timeline by resource group, project, booking type, or custom field values to focus on the work that matters.',
        },
        {
          title: 'View Controls',
          description: 'Switch between day, week, and month views. Use the date navigator to jump to any period quickly.',
        },
        {
          title: 'Copy & Paste',
          description: 'Select a booking and use the copy/paste controls to duplicate it to another resource or time slot.',
        },
        {
          title: 'Conflict Indicators',
          description: 'Overloaded resources are highlighted automatically. Open the conflict resolution panel to review and resolve overallocations.',
        },
      ],
      sectionsAndFields:
        'The planner grid shows resources as rows and dates as columns. Each booking block displays the project name, booking type, and duration. The header toolbar contains the date navigator, view toggles, and filter controls.',
    },
    userGuide: {
      intro: 'Use the Planner to build and manage your team\'s schedule. Follow the steps below to get started.',
      sections: [
        {
          title: 'How do I create a booking?',
          steps: [
            { step: 1, text: 'Click on any empty cell in the timeline grid for the resource and date you want to schedule.' },
            { step: 2, text: 'The booking form drawer opens on the right. Fill in the project, booking type, and date range.' },
            { step: 3, text: 'Enter the number of hours or select a percentage of daily capacity.' },
            { step: 4, text: 'Add any notes, custom fields, or set up a recurring pattern if needed.' },
            { step: 5, text: 'Click Save to create the booking. It will appear on the timeline immediately.' },
          ],
          note: 'If a project does not appear in the search, it may be inactive or you may not have permission to book against it. Contact your administrator.',
          noteType: 'warning',
        },
        {
          title: 'How do I edit an existing booking?',
          steps: [
            { step: 1, text: 'Click on the booking block in the timeline to open its details.' },
            { step: 2, text: 'The booking form opens pre-populated with all existing values.' },
            { step: 3, text: 'Make your changes and click Save. The timeline updates instantly.' },
          ],
        },
        {
          title: 'How do I move or reschedule a booking?',
          steps: [
            { step: 1, text: 'Hover over the booking block until a move cursor appears.' },
            { step: 2, text: 'Drag the booking to the desired resource row and date position.' },
            { step: 3, text: 'Release to drop. Conflicts are flagged if the target resource is overloaded.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I book the same resource on multiple projects at the same time?',
        answer: 'Yes. Overlapping bookings are allowed and tracked. When total daily allocation exceeds 100%, the resource row is highlighted and flagged in the utilisation heatmap.',
      },
      {
        question: 'How do I set up a recurring booking?',
        answer: 'Open the booking form and scroll to the Recurrence section. Choose Daily, Weekly, or Monthly patterns and set an end date or occurrence count.',
      },
      {
        question: 'Why can\'t I see a project in the booking form search?',
        answer: 'Projects that are inactive, archived, or that your account does not have permission to book against will not appear. Contact your planner administrator.',
      },
      {
        question: 'How do I delete a booking?',
        answer: 'Click the booking to open the form, then click the Delete button at the bottom of the drawer. You can delete a single occurrence or all instances of a recurring booking.',
      },
      {
        question: 'Can I undo an accidental drag-and-drop?',
        answer: 'Use Ctrl+Z (Windows) or Cmd+Z (Mac) immediately after a drag-and-drop to undo the move. Changes are reversible until the page is refreshed.',
      },
      {
        question: 'What does the colour coding on bookings mean?',
        answer: 'Booking colours are determined by the booking type assigned in Settings → Booking Types. You can customise these colours to represent different categories of work.',
      },
      {
        question: 'How do I filter by a specific resource group?',
        answer: 'Use the Filters toolbar above the planner grid. Select Resource Group from the filter options and choose the group(s) you want to display.',
      },
    ],
    tourSteps: [
      {
        id: 'planner-toolbar',
        title: 'Toolbar & Navigation',
        description: 'The toolbar lets you navigate dates, switch between Day, Week, and Month views, and access filters to narrow down what you see on the timeline.',
        targetSelector: '[data-tour="planner-calendar"]',
        placement: 'bottom',
        icon: 'CalendarDays',
      },
      {
        id: 'planner-grid',
        title: 'Timeline Grid',
        description: 'Each row is a resource. Each column is a date. Click any empty cell to create a booking. Drag booking blocks to move or resize them.',
        targetSelector: '[data-tour="planner-calendar"]',
        placement: 'bottom',
        icon: 'LayoutGrid',
      },
      {
        id: 'booking-form-open',
        title: 'Booking Form',
        description: 'Click any booking block or empty cell to open this form. Fill in project, type, dates, hours, custom fields, and recurrence settings.',
        targetSelector: '[data-tour="booking-form"]',
        placement: 'left',
        icon: 'PlusCircle',
      },
      {
        id: 'planner-filters',
        title: 'Filters',
        description: 'Narrow the planner view by resource group, project, booking type, or custom field. Use Search to find a specific resource quickly.',
        targetSelector: '[data-tour="planner-filters"]',
        placement: 'bottom',
        icon: 'Filter',
      },
    ],
    relatedPages: [
      { label: 'Heatmap', page: 'heatmap' },
      { label: 'Open Demand', page: 'unallocated' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Heatmap ────────────────────────────────────────────────────────────────
  heatmap: {
    pageTitle: 'Utilisation Heatmap',
    pageDescription: 'Visualise team capacity utilisation across time and quickly identify overloaded or under-utilised resources.',
    functionality: {
      overview:
        'The Heatmap provides a colour-coded bird\'s-eye view of how your team\'s capacity is being used. Each cell represents one resource for one time period. Colour intensity increases from green (under-utilised) through amber (approaching full) to red (overloaded), making bottlenecks immediately visible without drilling into individual calendars.',
      features: [
        {
          title: 'Colour-Coded Utilisation',
          description: 'Cell colours map to utilisation bands — green for under-used, amber for near-capacity, red for overloaded — so issues are visible at a glance.',
        },
        {
          title: 'Drill-Down View',
          description: 'Click any cell to see a breakdown of bookings for that resource and period, with project names and hours.',
        },
        {
          title: 'Burnout Risk Banner',
          description: 'When resources are trending toward sustained overload, an amber alert banner appears at the top with a count and a link to the conflict resolution panel.',
        },
        {
          title: 'Conflict Resolution Panel',
          description: 'Opens an interactive overlay showing overloaded resources, recommended reassignments, and the projected impact of each option before committing.',
        },
        {
          title: 'Date Range Controls',
          description: 'Adjust the visible time window from a single week up to a full quarter to get the view you need.',
        },
        {
          title: 'Resource Group Filter',
          description: 'Filter the heatmap to show only specific teams, departments, or resource groups.',
        },
        {
          title: 'Export',
          description: 'Export the heatmap data to CSV for use in reporting or capacity planning spreadsheets.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Heatmap to spot capacity issues early. The steps below guide you through the key workflows.',
      sections: [
        {
          title: 'How do I identify overloaded resources?',
          steps: [
            { step: 1, text: 'Look for red or deep-amber cells in the heatmap grid — these indicate over-allocation.' },
            { step: 2, text: 'Hover over a red cell to see the exact utilisation percentage and which bookings are causing the overload.' },
            { step: 3, text: 'Click the cell to open the drill-down view showing all bookings for that resource and period.' },
          ],
        },
        {
          title: 'How do I resolve a conflict?',
          steps: [
            { step: 1, text: 'Click the amber "Burnout Risk" banner at the top of the page, or click any overloaded cell.' },
            { step: 2, text: 'The Conflict Resolution overlay opens, showing the overloaded resource and their current bookings.' },
            { step: 3, text: 'Review the recommended reassignment options. Each option shows the projected utilisation after the change.' },
            { step: 4, text: 'Select your preferred resolution and click Apply. The change is reflected in the planner immediately.' },
          ],
          note: 'Conflict resolution changes are applied directly to bookings. You can undo immediately from the planner if the result is not what you expected.',
          noteType: 'info',
        },
      ],
    },
    faq: [
      {
        question: 'What do the different heatmap colours mean?',
        answer: 'Green cells indicate under-utilisation (below ~70%). Amber cells are approaching capacity (70–100%). Red cells are over-allocated (above 100%). The exact thresholds can be configured in Settings.',
      },
      {
        question: 'Why does a resource show as red when I know they have capacity?',
        answer: 'Overallocation is calculated from booked hours versus contracted hours. Check the resource\'s contracted weekly hours in the Resources page — they may be set lower than expected.',
      },
      {
        question: 'Can I adjust the utilisation thresholds for the colour bands?',
        answer: 'Yes. Navigate to Settings → General and look for the Utilisation Thresholds section to customise the amber and red breakpoints.',
      },
      {
        question: 'How far into the future does the heatmap show?',
        answer: 'The heatmap can display up to a 12-week rolling window. Use the date range controls at the top to move the window earlier or later.',
      },
      {
        question: 'How does the conflict resolution recommendation engine work?',
        answer: 'It matches the overloaded booking\'s required skills against available resources with spare capacity in the same period, then ranks candidates by availability score.',
      },
      {
        question: 'Can I export the heatmap data?',
        answer: 'Yes. Click the Export button in the toolbar to download the current heatmap view as a CSV file.',
      },
    ],
    tourSteps: [
      {
        id: 'heatmap-grid',
        title: 'Heatmap Grid',
        description: 'Each row is a resource, each column is a time period. Colour intensity shows utilisation — green is available, amber is near-full, red is overloaded.',
        targetSelector: '[data-tour="heatmap-grid"]',
        placement: 'bottom',
        icon: 'Activity',
      },
      {
        id: 'heatmap-burnout-banner',
        title: 'Burnout Risk Alert',
        description: 'This banner appears when one or more resources are trending toward sustained overload. Click it to jump straight into the Conflict Resolution panel.',
        targetSelector: '[data-tour="whatif-panel"]',
        placement: 'bottom',
        icon: 'AlertTriangle',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Utilisation Report', page: 'report-utilisation' },
    ],
  },

  // ─── Resources ──────────────────────────────────────────────────────────────
  resources: {
    pageTitle: 'Resources',
    pageDescription: 'Manage your team directory — view availability, skills, utilisation, and current assignments for every resource.',
    functionality: {
      overview:
        'The Resources page is your team directory. It gives you a complete view of every resource in the system — their role, department, skills, contracted hours, current utilisation rate, and upcoming bookings. Administrators can add, edit, and deactivate resources from this page.',
      features: [
        {
          title: 'Resource Directory',
          description: 'A searchable, filterable table of all resources with their role, department, and current availability status.',
        },
        {
          title: 'Utilisation Rate',
          description: 'Each resource shows their current utilisation percentage based on booked hours versus contracted capacity.',
        },
        {
          title: 'Skills & Attributes',
          description: 'View the skills and custom attributes assigned to each resource, which are used by the conflict resolution engine for recommendations.',
        },
        {
          title: 'Add Resource',
          description: 'Administrators can add new team members by clicking Add Resource and filling in their profile, role, and contracted hours.',
        },
        {
          title: 'Edit Resource',
          description: 'Click any resource row to view their full profile, edit details, manage skills, and view their booking history.',
        },
        {
          title: 'Filters & Search',
          description: 'Filter by department, role, availability, or skill to find the right person for a project quickly.',
        },
        {
          title: 'Export',
          description: 'Download the resource list with utilisation data as a CSV for workforce planning purposes.',
        },
      ],
    },
    userGuide: {
      intro: 'The Resources page lets you manage your team directory and view capacity at an individual level.',
      sections: [
        {
          title: 'How do I find a resource?',
          steps: [
            { step: 1, text: 'Use the Search box at the top of the table to find a resource by name.' },
            { step: 2, text: 'Use the Department or Role filters to narrow down by team or job function.' },
            { step: 3, text: 'Click on a resource row to view their full profile and booking schedule.' },
          ],
        },
        {
          title: 'How do I add a new resource?',
          steps: [
            { step: 1, text: 'Click the Add Resource button in the top-right corner of the page.' },
            { step: 2, text: 'Fill in the resource\'s name, role, department, and contracted weekly hours.' },
            { step: 3, text: 'Assign skills and any custom attributes required by your booking workflows.' },
            { step: 4, text: 'Click Save. The resource appears in the directory and becomes available for booking in the Planner.' },
          ],
          note: 'Only administrators can add or edit resources. If you do not see the Add Resource button, contact your system administrator.',
          noteType: 'warning',
        },
      ],
    },
    faq: [
      {
        question: 'How is the utilisation rate on the Resources page calculated?',
        answer: 'Utilisation is calculated as total booked hours divided by contracted weekly hours for the current rolling four-week period.',
      },
      {
        question: 'Can I deactivate a resource instead of deleting them?',
        answer: 'Yes. Open the resource profile and toggle the Active status. Inactive resources are hidden from the planner but their historical bookings are preserved.',
      },
      {
        question: 'How do I assign skills to a resource?',
        answer: 'Open the resource profile by clicking their row. Navigate to the Skills tab and add skills from the pre-defined list or create custom ones.',
      },
      {
        question: 'Can a resource belong to multiple departments?',
        answer: 'Resources have one primary department but can be assigned to multiple planner groups, allowing them to appear under different teams in the planner view.',
      },
      {
        question: 'What does "contracted hours" mean and why does it matter?',
        answer: 'Contracted hours define the maximum capacity of a resource per week. All utilisation percentages and overallocation warnings are based on this figure.',
      },
    ],
    tourSteps: [
      {
        id: 'resources-table',
        title: 'Resource Directory',
        description: 'Every row is a team member. You can see their role, department, utilisation rate, and availability at a glance. Click a row to open their full profile.',
        targetSelector: '[data-tour="resources-table"]',
        placement: 'bottom',
        icon: 'Users',
      },
      {
        id: 'resources-filters',
        title: 'Search & Filters',
        description: 'Use the search box and filter dropdowns to find resources by name, role, department, or skill set.',
        targetSelector: '[data-tour="resources-filters"]',
        placement: 'bottom',
        icon: 'Filter',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Resource Allocation Report', page: 'report-resource-allocation' },
    ],
  },

  // ─── Projects ───────────────────────────────────────────────────────────────
  projects: {
    pageTitle: 'Projects',
    pageDescription: 'Browse, manage, and review all projects that resources can be booked against.',
    functionality: {
      overview:
        'The Projects page lists every project in the system. For each project you can see its status, the total booked hours, and a list of assigned resources. Administrators can add new projects, edit existing ones, and sync with connected PSA systems.',
      features: [
        {
          title: 'Project Directory',
          description: 'A searchable table of all active, paused, and archived projects with status, owner, and utilisation summary.',
        },
        {
          title: 'Project Detail',
          description: 'Click any project to open its detail page showing all bookings, assigned resources, and a mini-timeline.',
        },
        {
          title: 'Add Project',
          description: 'Create a new project manually by clicking Add Project and filling in the name, code, owner, and billing details.',
        },
        {
          title: 'PSA Sync',
          description: 'If a PSA integration (e.g. ConnectWise, Autotask) is configured, projects can be synced automatically from the external system.',
        },
        {
          title: 'Status Filtering',
          description: 'Filter by Active, On Hold, or Archived to focus on the projects relevant to your current planning cycle.',
        },
        {
          title: 'Search',
          description: 'Search by project name, code, or client name to find a specific project quickly.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Projects page to manage the projects your team works on and to review their scheduling status.',
      sections: [
        {
          title: 'How do I add a new project?',
          steps: [
            { step: 1, text: 'Click Add Project in the top-right corner of the page.' },
            { step: 2, text: 'Enter the project name, project code, client, owner, and start/end dates.' },
            { step: 3, text: 'Set the project status to Active to make it available for booking in the Planner.' },
            { step: 4, text: 'Click Save. The project is now searchable in the Planner booking form.' },
          ],
          note: 'Project codes must be unique. If a code is already in use, the system will prompt you to enter a different one.',
          noteType: 'info',
        },
        {
          title: 'How do I view all bookings for a project?',
          steps: [
            { step: 1, text: 'Find the project in the list using search or filters.' },
            { step: 2, text: 'Click the project row to open its detail page.' },
            { step: 3, text: 'The detail page shows all resources currently booked on the project, total hours, and a timeline view.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Why doesn\'t a project appear in the Planner booking form?',
        answer: 'Only Active projects appear in the booking form search. Check that the project status is set to Active on this page.',
      },
      {
        question: 'Can I archive a project without deleting its bookings?',
        answer: 'Yes. Setting a project to Archived removes it from new booking searches but preserves all historical bookings for reporting purposes.',
      },
      {
        question: 'How does PSA sync work?',
        answer: 'When a PSA integration is configured in Settings → Integrations, projects are synced on a scheduled basis or on demand. New projects from the PSA appear automatically in this list.',
      },
      {
        question: 'Can I set a budget or target hours for a project?',
        answer: 'Yes. Open the project detail page and look for the Budget section where you can enter planned hours. The project staffing report will track actuals against this budget.',
      },
    ],
    tourSteps: [
      {
        id: 'projects-list',
        title: 'Project List',
        description: 'Every row is a project. You can see the status, owner, and total booked hours. Click a row to open the project detail view.',
        targetSelector: '[data-tour="projects-list"]',
        placement: 'bottom',
        icon: 'FolderKanban',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Project Staffing Report', page: 'report-project-staffing' },
    ],
  },

  // ─── Settings ───────────────────────────────────────────────────────────────
  settings: {
    pageTitle: 'Settings',
    pageDescription: 'Configure booking types, working hours, user permissions, integrations, and planner behaviour for your organisation.',
    functionality: {
      overview:
        'Settings is the administration hub for Scheduler. Here administrators can define booking types and their visual styles, configure contracted working hours, manage user roles and permissions, and set up integrations with external PSA systems. Personal preferences such as notifications and display options are also managed here.',
      features: [
        {
          title: 'Booking Types',
          description: 'Create and manage the booking categories used throughout the planner — such as Billable, Internal, Leave, or Training — with custom colours and field configurations.',
        },
        {
          title: 'Working Hours',
          description: 'Define standard working days and hours for your organisation, used as the baseline for utilisation calculations and capacity planning.',
        },
        {
          title: 'Users & Roles',
          description: 'Invite team members, assign roles (Admin, Planner, Viewer), and manage access permissions per resource group.',
        },
        {
          title: 'Integrations',
          description: 'Connect Scheduler to your PSA system (ConnectWise, Autotask, HaloPSA) to automatically sync projects and resources.',
        },
        {
          title: 'Planner Groups',
          description: 'Organise resources into logical groups for filtering and display in the planner timeline.',
        },
        {
          title: 'Notifications',
          description: 'Configure email notification rules for booking approvals, rejections, and capacity warnings.',
        },
        {
          title: 'Custom Fields',
          description: 'Add custom data fields to the booking form that capture information specific to your team\'s workflows.',
        },
      ],
    },
    userGuide: {
      intro: 'Use Settings to tailor Scheduler to your organisation\'s processes and preferences.',
      sections: [
        {
          title: 'How do I create a new booking type?',
          steps: [
            { step: 1, text: 'Navigate to the Booking Types tab in Settings.' },
            { step: 2, text: 'Click Add Booking Type and enter a name and description.' },
            { step: 3, text: 'Choose a colour — this will be used to display bookings of this type in the planner.' },
            { step: 4, text: 'Configure any custom fields or options specific to this booking type.' },
            { step: 5, text: 'Click Save. The booking type is immediately available in the Planner booking form.' },
          ],
        },
        {
          title: 'How do I connect a PSA integration?',
          steps: [
            { step: 1, text: 'Go to the Integrations tab in Settings.' },
            { step: 2, text: 'Select your PSA system from the list (ConnectWise, Autotask, HaloPSA, etc.).' },
            { step: 3, text: 'Enter your API credentials as directed by the on-screen guide.' },
            { step: 4, text: 'Click Test Connection to verify the credentials are correct.' },
            { step: 5, text: 'Click Save & Sync to import projects and resources from your PSA.' },
          ],
          note: 'You will need API credentials from your PSA system. Contact your PSA administrator if you do not have these.',
          noteType: 'info',
        },
      ],
    },
    faq: [
      {
        question: 'Who can access the Settings page?',
        answer: 'Only users with the Admin role can access and modify Settings. Planner and Viewer roles cannot see this page.',
      },
      {
        question: 'Will changing working hours affect existing bookings?',
        answer: 'Changing working hours updates future utilisation calculations but does not retroactively alter existing booking data.',
      },
      {
        question: 'Can I create different booking types for different teams?',
        answer: 'Yes. Booking types can be restricted to specific planner groups, so only the relevant teams see them in the booking form.',
      },
      {
        question: 'How often does the PSA sync run automatically?',
        answer: 'The default sync frequency is every 4 hours. You can change this in the Integrations tab, or trigger a manual sync at any time.',
      },
      {
        question: 'What happens if I delete a booking type that has existing bookings?',
        answer: 'You cannot delete a booking type that has active bookings. Archive it instead — this hides it from new bookings while preserving historical data.',
      },
    ],
    tourSteps: [
      {
        id: 'settings-tabs',
        title: 'Settings Tabs',
        description: 'Navigate between General, Booking Types, Working Hours, Users, Integrations, and more using the tab bar at the top of the page.',
        targetSelector: '[data-tour="settings"]',
        placement: 'bottom',
        icon: 'Settings',
      },
    ],
    relatedPages: [
      { label: 'Resources', page: 'resources' },
      { label: 'Planner', page: 'calendar' },
    ],
  },

  // ─── Unallocated Bookings ───────────────────────────────────────────────────
  unallocated: {
    pageTitle: 'Open Demand',
    pageDescription: 'View and allocate all bookings that have not yet been assigned to a specific resource.',
    functionality: {
      overview:
        'The Open Demand page surfaces all bookings in the system that have no resource assigned. They represent unfulfilled project demand and are colour-coded by urgency — red for work starting within 3 days, amber for within 7 days, and standard for everything else. From here you can suggest a resource, assign manually, or jump to the planner to place the booking yourself.',
      features: [
        {
          title: 'Urgency Colour Coding',
          description: 'Bookings starting within 3 days are flagged red. Those starting within 7 days are amber. This lets you prioritise urgent allocations at a glance.',
        },
        {
          title: 'Suggest Resource',
          description: 'Opens the smart allocation form for the selected booking. The system recommends the best available resource based on skills and current capacity.',
        },
        {
          title: 'View in Planner',
          description: 'Jump directly to the Planner with the selected booking highlighted, so you can manually choose where to place it.',
        },
        {
          title: 'Filters',
          description: 'Filter open demand by project, booking type, urgency level, or date range to focus on the most important unallocated work.',
        },
        {
          title: 'Bulk Actions',
          description: 'Select multiple unallocated bookings and apply bulk actions such as reassigning to a resource or changing the date range.',
        },
      ],
    },
    userGuide: {
      intro: 'Use Open Demand to manage unallocated work and ensure nothing falls through the gaps.',
      sections: [
        {
          title: 'How do I allocate an unallocated booking?',
          steps: [
            { step: 1, text: 'Find the booking in the list — use filters or scroll to locate it.' },
            { step: 2, text: 'Click Suggest Resource to open the allocation form with smart recommendations.' },
            { step: 3, text: 'Review the suggested resources and their available capacity for the booking period.' },
            { step: 4, text: 'Select your preferred resource and click Assign. The booking is moved to their calendar.' },
          ],
          note: 'If no suitable resource is suggested, the booking may have skills requirements that no available resource meets. Check the resource skills in the Resources page.',
          noteType: 'warning',
        },
        {
          title: 'How do I view an unallocated booking in the Planner?',
          steps: [
            { step: 1, text: 'Click View in Planner next to the booking you want to locate.' },
            { step: 2, text: 'The Planner opens with the date range set to the booking period.' },
            { step: 3, text: 'Manually drag the booking onto the desired resource row to allocate it.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Where do unallocated bookings come from?',
        answer: 'They can be created manually in the Planner by saving a booking without selecting a resource, or imported from a connected PSA system as project demand.',
      },
      {
        question: 'Why is a booking showing in red?',
        answer: 'Red bookings are starting within the next 3 days and have not yet been allocated. These require urgent attention to avoid project delivery delays.',
      },
      {
        question: 'Can I delete an unallocated booking from this page?',
        answer: 'Yes. Click the booking row to expand it and use the Delete option. This permanently removes the demand record.',
      },
      {
        question: 'How does the smart resource suggestion work?',
        answer: 'The engine filters resources by required skills, then ranks them by available capacity in the booking\'s date range, weighted by their overall utilisation trend.',
      },
    ],
    tourSteps: [
      {
        id: 'unallocated-list',
        title: 'Open Demand List',
        description: 'Each row is an unallocated booking. Red rows are urgent (starting within 3 days). Amber rows start within 7 days. Use Suggest Resource to allocate quickly.',
        targetSelector: '[data-tour="unallocated-list"]',
        placement: 'right',
        icon: 'Inbox',
      },
    ],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Overview ───────────────────────────────────────────────────────────────
  overview: {
    pageTitle: 'Dashboard Overview',
    pageDescription: 'A high-level summary of team utilisation, open demand, and recent scheduling activity.',
    functionality: {
      overview:
        'The Dashboard Overview provides a snapshot of your team\'s current state. KPI cards show total resources, open demand count, average utilisation, and overloaded resources at a glance. Charts give trend data, and quick-action links let you jump to the most common tasks without navigating through the sidebar.',
      features: [
        {
          title: 'KPI Cards',
          description: 'At-a-glance metrics for total resources, current open demand, average team utilisation, and number of overloaded resources.',
        },
        {
          title: 'Utilisation Chart',
          description: 'A bar or line chart showing utilisation trends over the past 4–8 weeks to spot patterns early.',
        },
        {
          title: 'Open Demand Summary',
          description: 'A count and breakdown of unallocated bookings by urgency, with a direct link to the Open Demand page.',
        },
        {
          title: 'Quick Actions',
          description: 'Shortcut buttons to create a new booking, view the heatmap, or navigate to the most overloaded resources.',
        },
      ],
    },
    userGuide: {
      intro: 'The Overview gives you a daily health check of your team\'s scheduling status.',
      sections: [
        {
          title: 'How do I use the Overview dashboard?',
          steps: [
            { step: 1, text: 'Review the KPI cards at the top — these give you an instant summary of the team\'s status.' },
            { step: 2, text: 'Check the utilisation trend chart to see if the team is trending up or down in workload.' },
            { step: 3, text: 'If open demand is high, click the Open Demand summary to go directly to the allocation page.' },
            { step: 4, text: 'Use the Quick Action buttons to navigate to the most relevant part of the planner.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'How often does the Overview data refresh?',
        answer: 'Overview data refreshes automatically every time you navigate to the page. You can also force a refresh using your browser\'s reload button.',
      },
      {
        question: 'Can I customise which KPIs appear on the Overview?',
        answer: 'Custom KPI configuration is on the roadmap. Currently the displayed metrics are fixed but reflect the most commonly needed planning indicators.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Planner', page: 'calendar' },
      { label: 'Heatmap', page: 'heatmap' },
    ],
  },

  // ─── Utilisation Report ─────────────────────────────────────────────────────
  'report-utilisation': {
    pageTitle: 'Utilisation Report',
    pageDescription: 'Review historical and current utilisation rates across resources, groups, and projects.',
    functionality: {
      overview:
        'The Utilisation Report provides a detailed breakdown of how team capacity has been used across a selected date range. You can view utilisation by individual resource, by planner group, or rolled up to organisation level. The report is used for billing reviews, performance tracking, and capacity trend analysis.',
      features: [
        {
          title: 'Date Range Selector',
          description: 'Choose any historical or future date range for the report. Pre-sets include This Week, Last Month, Last Quarter, and custom ranges.',
        },
        {
          title: 'Resource & Group Filters',
          description: 'Narrow the report to specific individuals, teams, or booking types.',
        },
        {
          title: 'Billable vs Non-Billable',
          description: 'Split utilisation into billable and non-billable categories based on booking type configuration.',
        },
        {
          title: 'CSV Export',
          description: 'Download the report data as a CSV for use in external reporting tools or spreadsheets.',
        },
        {
          title: 'Heatmap Link',
          description: 'Click any resource row to jump directly to the heatmap filtered to that resource for deeper capacity analysis.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to review team utilisation over any time period.',
      sections: [
        {
          title: 'How do I run the Utilisation Report?',
          steps: [
            { step: 1, text: 'Set the date range using the date picker at the top of the page.' },
            { step: 2, text: 'Optionally apply resource group or booking type filters.' },
            { step: 3, text: 'The report table updates automatically with utilisation percentages and hours.' },
            { step: 4, text: 'Click Export to download the results as CSV.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'What is the difference between utilisation and capacity?',
        answer: 'Capacity is the total available hours (contracted hours × working days). Utilisation is the percentage of that capacity that has been booked.',
      },
      {
        question: 'Can I see billable utilisation separately?',
        answer: 'Yes. Use the Booking Type filter to include only billable booking types, or enable the Billable/Non-Billable split toggle.',
      },
    ],
    tourSteps: [
      {
        id: 'report-util-table',
        title: 'Utilisation Table',
        description: 'Each row shows a resource\'s utilisation percentage, total hours booked, and available capacity for the selected date range.',
        targetSelector: '[data-tour="reports-suite"]',
        placement: 'bottom',
        icon: 'BarChart2',
      },
    ],
    relatedPages: [
      { label: 'Heatmap', page: 'heatmap' },
      { label: 'Utilisation Forecast', page: 'report-utilisation-forecast' },
    ],
  },

  // ─── Utilisation Forecast ───────────────────────────────────────────────────
  'report-utilisation-forecast': {
    pageTitle: 'Utilisation Forecast',
    pageDescription: 'Project future utilisation based on confirmed bookings to identify capacity gaps before they become problems.',
    functionality: {
      overview:
        'The Utilisation Forecast report projects how your team\'s capacity will be used over the coming weeks and months, based on current confirmed bookings. It helps you identify future over-allocation and under-utilisation early, so you can adjust the schedule or resources before it becomes a problem.',
      features: [
        {
          title: 'Forward-Looking Timeline',
          description: 'View projected utilisation week-by-week for the next 4 to 26 weeks.',
        },
        {
          title: 'Capacity Gap Detection',
          description: 'Periods where utilisation exceeds 100% or drops below a configurable minimum are highlighted automatically.',
        },
        {
          title: 'Resource & Group Breakdown',
          description: 'Drill down from team-level forecast to individual resource forecasts.',
        },
        {
          title: 'CSV Export',
          description: 'Export forecast data for capacity planning meetings or external reporting.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Forecast report to plan ahead and prevent capacity crises.',
      sections: [
        {
          title: 'How do I read the forecast?',
          steps: [
            { step: 1, text: 'Select the forecast window — choose from 4, 8, 13, or 26 weeks ahead.' },
            { step: 2, text: 'Look for red-highlighted weeks where team utilisation exceeds 100%.' },
            { step: 3, text: 'Click any highlighted week to drill down to the individual resource level.' },
            { step: 4, text: 'Use the Planner to add or remove bookings to balance the forecast.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'How far ahead does the forecast go?',
        answer: 'The forecast shows up to 26 weeks ahead based on confirmed bookings. It does not include tentative or draft bookings unless that filter is enabled.',
      },
      {
        question: 'Does the forecast update when I add new bookings?',
        answer: 'Yes. The forecast is recalculated in real time whenever a booking is added, changed, or deleted.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Utilisation Report', page: 'report-utilisation' },
      { label: 'Heatmap', page: 'heatmap' },
    ],
  },

  // ─── Available Capacity ─────────────────────────────────────────────────────
  'report-available-capacity': {
    pageTitle: 'Available Capacity',
    pageDescription: 'Find resources with available hours in a specific period to fulfil incoming project demand.',
    functionality: {
      overview:
        'The Available Capacity report helps you quickly find who has spare capacity within a given date range. This is typically used when a new project needs to be staffed — enter the required hours and skills, and the report returns a ranked list of resources who can take the work.',
      features: [
        {
          title: 'Date Range Search',
          description: 'Specify the period you need coverage for and the report calculates remaining capacity per resource.',
        },
        {
          title: 'Hours Filter',
          description: 'Filter to show only resources with at least a specified number of available hours in the period.',
        },
        {
          title: 'Skills Filter',
          description: 'Narrow results to resources with specific skills required for the project.',
        },
        {
          title: 'Book Directly',
          description: 'Click any resource in the results to open the Planner booking form pre-populated with their details and the selected date range.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to staff new projects quickly by finding who has the right capacity.',
      sections: [
        {
          title: 'How do I find available resources?',
          steps: [
            { step: 1, text: 'Set the date range for the period you need to staff.' },
            { step: 2, text: 'Enter the minimum hours required.' },
            { step: 3, text: 'Optionally add a skills filter to match project requirements.' },
            { step: 4, text: 'Click Search. The table shows resources ranked by available hours.' },
            { step: 5, text: 'Click Book next to any resource to create a booking directly.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Does Available Capacity include part-time workers?',
        answer: 'Yes. Capacity is calculated from each resource\'s individual contracted hours, so part-time resources show proportionally lower available hours.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Resources', page: 'resources' },
      { label: 'Planner', page: 'calendar' },
    ],
  },

  // ─── Resource Allocation ────────────────────────────────────────────────────
  'report-resource-allocation': {
    pageTitle: 'Resource Allocation',
    pageDescription: 'Review how each resource\'s time is allocated across projects and booking types for a given period.',
    functionality: {
      overview:
        'The Resource Allocation report shows a detailed breakdown of how each resource has spent their time, split by project and booking type. It is used to review whether resources are spending their time aligned to business priorities and to generate data for invoicing or cost allocation.',
      features: [
        {
          title: 'Per-Resource Breakdown',
          description: 'See hours and percentages split by project and booking type for each resource.',
        },
        {
          title: 'Time Period Filters',
          description: 'Filter by week, month, quarter, or custom date range.',
        },
        {
          title: 'Project & Group Filters',
          description: 'Narrow to a specific project, client, or resource group.',
        },
        {
          title: 'CSV Export',
          description: 'Export for use in payroll, invoicing, or management reporting.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report for billing, time tracking, and resource performance reviews.',
      sections: [
        {
          title: 'How do I run the Resource Allocation Report?',
          steps: [
            { step: 1, text: 'Select the date range you want to report on.' },
            { step: 2, text: 'Optionally filter by resource group or project.' },
            { step: 3, text: 'Review the allocation table. Each row breaks down one resource\'s time by project.' },
            { step: 4, text: 'Click Export to download the data as CSV.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I filter this report to just billable hours?',
        answer: 'Yes. Use the Booking Type filter and select only the booking types classified as billable.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Utilisation Report', page: 'report-utilisation' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Project Staffing ───────────────────────────────────────────────────────
  'report-project-staffing': {
    pageTitle: 'Project Staffing',
    pageDescription: 'Review staffing levels and hours allocation across individual projects.',
    functionality: {
      overview:
        'The Project Staffing report focuses on the project dimension — showing how many resources are assigned to each project, how many hours have been booked, and how that compares to the planned budget. It is the primary tool for project managers to track whether projects are on track from a staffing perspective.',
      features: [
        {
          title: 'Project-by-Project View',
          description: 'Each project is a row showing resource count, total booked hours, and percentage of budget consumed.',
        },
        {
          title: 'Budget vs Actuals',
          description: 'Compare booked hours to the project budget target to spot over-servicing or under-servicing early.',
        },
        {
          title: 'Resource Drill-Down',
          description: 'Click any project to see the breakdown by individual resource, showing hours by person.',
        },
        {
          title: 'Navigate to Heatmap',
          description: 'Click View in Heatmap to jump directly to the heatmap filtered to that project.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this report to keep project staffing levels aligned with delivery plans.',
      sections: [
        {
          title: 'How do I check if a project is over-resourced?',
          steps: [
            { step: 1, text: 'Find the project in the list using search or filters.' },
            { step: 2, text: 'Check the Budget vs Actuals column — a figure above 100% means booked hours exceed the planned budget.' },
            { step: 3, text: 'Click the project row to see the breakdown by resource.' },
            { step: 4, text: 'Navigate to the Planner to remove or reduce bookings as needed.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Where does the project budget figure come from?',
        answer: 'Budget hours are set on the project detail page. If no budget is set, the Budget vs Actuals column will show "No budget" for that project.',
      },
      {
        question: 'Can I see project staffing for future periods?',
        answer: 'Yes. Set the date range to a future period to see planned staffing based on current confirmed bookings.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Projects', page: 'projects' },
      { label: 'Available Capacity', page: 'report-available-capacity' },
    ],
  },

  // ─── Bench Report ───────────────────────────────────────────────────────────
  'report-bench': {
    pageTitle: 'Bench Report',
    pageDescription: 'Identify resources with little or no bookings in an upcoming period — those currently "on the bench".',
    functionality: {
      overview:
        'The Bench Report identifies resources who have zero or minimal bookings in a specified upcoming period. It helps resource managers proactively find under-utilised team members who could be assigned to new projects or have their time re-planned before the period begins.',
      features: [
        {
          title: 'Bench Detection',
          description: 'Automatically flags resources with utilisation below a configurable threshold for the selected period.',
        },
        {
          title: 'Date Range Controls',
          description: 'Select any upcoming period to check who is available and under-booked.',
        },
        {
          title: 'Threshold Filter',
          description: 'Set a custom utilisation threshold — e.g. show all resources below 50% booked.',
        },
        {
          title: 'Quick Actions',
          description: 'Click any benched resource to jump to their availability in the Available Capacity report or open the Planner.',
        },
      ],
    },
    userGuide: {
      intro: 'Use the Bench Report to find resources who need new work assigned before they become under-utilised.',
      sections: [
        {
          title: 'How do I find under-utilised resources?',
          steps: [
            { step: 1, text: 'Set the date range to the upcoming period you want to review (e.g. next 2 weeks).' },
            { step: 2, text: 'Adjust the bench threshold if needed (default is resources below 50% utilised).' },
            { step: 3, text: 'Review the list of benched resources, sorted by lowest utilisation first.' },
            { step: 4, text: 'Click a resource to view their availability and assign new bookings.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'What counts as "on the bench"?',
        answer: 'A resource is considered benched when their projected utilisation for the selected period falls below the configured threshold (default: 50%).',
      },
      {
        question: 'How do I change the bench threshold?',
        answer: 'Use the Threshold slider or input at the top of the report page to adjust the percentage cutoff.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Available Capacity', page: 'report-available-capacity' },
      { label: 'Resources', page: 'resources' },
    ],
  },

  // ─── Roles & Permissions ───────────────────────────────────────────────────
  'roles-permissions': {
    pageTitle: 'Roles & Permissions',
    pageDescription: 'Define user roles and configure what each role can see and do within Scheduler.',
    functionality: {
      overview:
        'The Roles & Permissions page lets administrators define granular access control for the application. Each role is a named collection of permissions — controlling which pages, actions, and data a user of that role can access. Changes take effect immediately when a user\'s role is updated.',
      features: [
        {
          title: 'Role Management',
          description: 'Create custom roles or edit the default Admin, Planner, and Viewer roles to match your organisation\'s access requirements.',
        },
        {
          title: 'Permission Matrix',
          description: 'A clear table of all available permissions, showing which are enabled or disabled for each role.',
        },
        {
          title: 'Assign Roles',
          description: 'Assign roles to users from the Users section of Settings or directly from this page.',
        },
      ],
    },
    userGuide: {
      intro: 'Use this page to control who can do what in Scheduler.',
      sections: [
        {
          title: 'How do I create a custom role?',
          steps: [
            { step: 1, text: 'Click Create Role at the top of the page.' },
            { step: 2, text: 'Give the role a name and description.' },
            { step: 3, text: 'Toggle each permission on or off in the permission matrix.' },
            { step: 4, text: 'Click Save Role. The role is now available to assign to users.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I edit the built-in Admin role?',
        answer: 'The Admin role has all permissions and cannot be restricted. You can create a custom role with a subset of admin permissions if needed.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Settings', page: 'settings' },
    ],
  },

  // ─── User Setup ─────────────────────────────────────────────────────────────
  'user-setup': {
    pageTitle: 'User Setup',
    pageDescription: 'Complete your personal profile and preferences for Scheduler.',
    functionality: {
      overview:
        'User Setup lets you configure your personal profile within Scheduler — including your display name, preferred timezone, notification preferences, and default planner view. This page is typically shown on first login but can be revisited from the profile menu at any time.',
      features: [
        {
          title: 'Profile Information',
          description: 'Update your display name, email, and profile avatar.',
        },
        {
          title: 'Timezone',
          description: 'Set your local timezone so booking times are displayed correctly for your location.',
        },
        {
          title: 'Default View',
          description: 'Choose your preferred default planner view — Daily, Weekly, or Monthly — so the planner always opens the way you like it.',
        },
        {
          title: 'Notification Preferences',
          description: 'Configure which email notifications you receive for booking events such as approvals, rejections, and reminders.',
        },
      ],
    },
    userGuide: {
      intro: 'Complete your setup to personalise your Scheduler experience.',
      sections: [
        {
          title: 'How do I complete my user setup?',
          steps: [
            { step: 1, text: 'Review your profile information and update your display name if needed.' },
            { step: 2, text: 'Select your timezone from the dropdown.' },
            { step: 3, text: 'Choose your preferred default planner view.' },
            { step: 4, text: 'Configure your notification preferences.' },
            { step: 5, text: 'Click Save to apply your settings.' },
          ],
        },
      ],
    },
    faq: [
      {
        question: 'Can I change my timezone after initial setup?',
        answer: 'Yes. Return to this page from the profile menu in the top navigation and update your timezone at any time.',
      },
    ],
    tourSteps: [],
    relatedPages: [
      { label: 'Settings', page: 'settings' },
    ],
  },
};

// Helper to get content for the current page, falling back to calendar
export function getHelpContent(page: string): PageHelpContent {
  return HELP_CONTENT[page as HelpPageKey] ?? HELP_CONTENT['calendar'];
}
